import React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  PixelAlert,
  PixelButton,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelImage,
  PixelInput,
  PixelText,
} from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'

import {
  verifyResetPassword,
  VerifyResetPasswordResponse,
  verifyResetPasswordToken,
} from 'utils/api-routes.util'

import { IPixelFactory } from '../../../../decl.d'
import logo from '../assets/newpixl.svg'

const ResetPassword = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [internalError, setError] = React.useState({
    enable: false,
    error: '',
  })
  const [data, setData] = React.useState({
    token: '',
    password: '',
    validated: false,
  })
  const [loading, setLoading] = React.useState(true)
  const [success, setSuccess] = React.useState<string | boolean>(false)
  const token = searchParams.get('token')
  const { showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)

  React.useEffect(() => {
    if (token) {
      setData({ ...data, token })
      verifyToken(token)
    } else {
      setLoading(false)
    }
  }, [token])

  const verifyToken = (token: string) => {
    showPageLoader()
    verifyResetPasswordToken(token).subscribe(
      (res: AjaxResponse<VerifyResetPasswordResponse>) => {
        if (res.response.validated) {
          setData({
            ...data,
            token,
            validated: res.response.validated,
          })
        } else {
          setError({
            enable: true,
            error: res.response.error || '',
          })
          setData({
            ...data,
            validated: false,
          })
        }
        setLoading(false)
        hidePageLoader()
      },
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    dispatchVerify(data)
  }

  const dispatchVerify = (data: { token: string; password: string }) => {
    showPageLoader()
    verifyResetPassword({
      token: data.token,
      password: data.password,
    }).subscribe((res: AjaxResponse<VerifyResetPasswordResponse>) => {
      if (res.response.message) {
        setSuccess(res.response.message)
        setTimeout(() => {
          navigate('/auth/login')
        }, 1000)
      } else if (res.response.error) {
        setError({
          enable: true,
          error: res.response.error || '',
        })
      }
      hidePageLoader()
    })
  }

  if (loading) {
    return (
      <PixelFlexBox
        width="100%"
        height="100vh"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
      >
        <div data-testid="reset-password-loading" />
      </PixelFlexBox>
    )
  }

  return (
    <>
      <PixelFlexBox
        width="100%"
        height="100vh"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
      >
        <PixelFlexBox
          width="100%"
          maxWidth="1600px"
          height="100vh"
          flexDirection="column"
          justifyContent="center"
          padding="0px 20px"
          alignItems="center"
        >
          <PixelImage height="50px" style={{ margin: '20px 0' }} src={logo} />
          <PixelText
            textSize="33px"
            style={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              margin: '20px 0',
            }}
          >
            Reset Password
          </PixelText>
          <PixelText
            customColor="#4d6575"
            style={{
              lineHeight: '16px',
              fontWeight: '400',
              marginBottom: '15px',
              maxWidth: '600px',
            }}
            textSize="16px"
          >
            {'Not a problem, we got you covered. Change your password.'}
          </PixelText>
          {data.validated ? (
            <form
              style={{
                maxWidth: '400px',
                width: '100%',
                textAlign: 'center',
              }}
              onSubmit={handleSubmit}
            >
              <PixelDiv
                width="100%"
                height="auto"
                style={{
                  display: 'grid !important',
                  marginBottom: '10px',
                  gridRowGap: '10px',
                }}
              >
                <PixelInput
                  type="password"
                  name="password"
                  required
                  value={data.password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setData({
                      ...data,
                      password: e.currentTarget.value,
                    })
                  }
                  placeholder="Password"
                />
              </PixelDiv>
              <PixelDiv
                width="100%"
                height="auto"
                style={{
                  display: 'grid !important',
                  marginBottom: '10px',
                  gridRowGap: '10px',
                }}
              >
                {internalError.enable && (
                  <PixelText customColor="red" textSize="80%">
                    {internalError.error}
                  </PixelText>
                )}
                {success && (
                  <PixelAlert variant="success">
                    {success}. You will be redirected to login...
                  </PixelAlert>
                )}
                <PixelButton type="submit" variant="primary">
                  Change Password
                </PixelButton>
              </PixelDiv>
            </form>
          ) : (
            <PixelFlexBox flexDirection="column" alignItems="center" gap="20px">
              <PixelText
                customColor="red"
                textSize="16px"
                data-testid="token-expired-message"
              >
                Token Expired.
              </PixelText>
              <Link to="/auth/login">
                <PixelButton variant="primary">Request Again</PixelButton>
              </Link>
            </PixelFlexBox>
          )}
        </PixelFlexBox>
      </PixelFlexBox>
    </>
  )
}

export default ResetPassword
